import * as Sentry from '@sentry/react';
import { env } from '../config/env';

/**
 * Initialize Sentry error tracking.
 *
 * Skips initialization if Sentry DSN is not configured (null or empty).
 * Configures:
 * - Browser tracing for performance monitoring
 * - Session replay for debugging (sample rates: 0.1 in production, 1.0 in staging)
 * - Release version from env.appVersion
 * - Environment from env.sentryEnvironment
 */
export function initErrorTracking(): void {
  // Skip initialization if DSN is not configured
  if (!env.sentryDsn) {
    console.info('[Error Tracking] Sentry DSN not configured, skipping initialization');
    return;
  }

  // Determine replay sample rate based on environment
  const replaySampleRate = env.environment === 'production' ? 0.1 : 1.0;

  Sentry.init({
    dsn: env.sentryDsn,
    environment: env.sentryEnvironment,
    release: env.appVersion,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    // Performance Monitoring sample rate (100% in all environments)
    tracesSampleRate: 1.0,
    // Session Replay sample rates
    replaysSessionSampleRate: replaySampleRate,
    replaysOnErrorSampleRate: 1.0, // Always capture replay on error
  });

  console.info('[Error Tracking] Sentry initialized', {
    environment: env.sentryEnvironment,
    release: env.appVersion,
    replaySampleRate,
  });
}

/**
 * Set user context for error tracking.
 *
 * Attaches call/customer/room metadata to all subsequent error reports.
 * Useful for debugging user-specific issues.
 *
 * @param context - User context with optional callId, customerId, roomKey
 */
export function setUserContext(context: {
  callId?: number;
  customerId?: number;
  roomKey?: string;
}): void {
  Sentry.setUser({
    id: context.customerId?.toString(),
    username: context.customerId?.toString(),
    call_id: context.callId?.toString(),
    room_key: context.roomKey,
  });
}

/**
 * Capture an error and send to Sentry.
 *
 * Wrapper around Sentry.captureException with optional extra context.
 *
 * @param error - Error object to capture
 * @param extra - Optional additional context to attach to the error
 */
export function captureError(
  error: Error,
  extra?: Record<string, unknown>
): void {
  if (extra) {
    Sentry.captureException(error, { extra });
  } else {
    Sentry.captureException(error);
  }
}
